<script setup lang="ts">
/**
 * PrivacyFooter — componente compartilhado de privacidade visível.
 *
 * Sempre presente em todos os estados canônicos. Mostra:
 * - Trust line literal: "Tudo local · LLM nunca vê θ_lat, memória, nome ou goals"
 * - Privacy badge ativo (verde) com pulso suave quando local
 * - HLOS-188: build identifier (versão do bundle) para diagnóstico
 *
 * Constitucional:
 * - CL-09 (Explicabilidade): torna o estado de privacidade auditável
 * - CC-09: privacy disclosure explícita
 * - Princípio X (Dignidade): privacidade visível em cada tela
 * - Princípio XI (Companion ≠ LLM): explicita o que o LLM nunca vê
 * - CC-06 (sem dark patterns): pulso suave (opacity 0.6→1, 2s) é
 *   INFORMATIVO (indica estado seguro), não manipulativo
 *
 * ⚠️ O pulso NÃO é dark pattern: é indicador técnico de "vault local ativo".
 * Diferente de "limited time!" ou "streak ending!".
 */
import { usePrivacyDisclosure } from "../composables/usePrivacyDisclosure";

const privacy = usePrivacyDisclosure();
// HLOS-188: build identifier injetado em build time (vite.config.ts).
// Permite ao usuário verificar qual versão do bundle está rodando.
// HLOS_BUILD_ID é definido em vite.config.ts via define.
const BUILD_ID: string = (import.meta.env as { HLOS_BUILD_ID?: string })
  .HLOS_BUILD_ID ?? "dev";
</script>

<template>
  <footer
    class="hlos-privacy-footer"
    data-testid="privacy-footer"
    role="contentinfo"
    aria-label="Informação de privacidade"
  >
    <div class="hlos-privacy-footer__row">
      <!--
        Privacy badge: indicador visual de "vault local ativo".
        Pulso suave (não piscante agressivo). Cor verde por convenção
        (semáforo positivo). aria-label descreve estado para leitores
        de tela.
      -->
      <span
        class="hlos-privacy-footer__badge"
        data-testid="privacy-badge"
        :data-local="privacy.isLocal.value ? 'true' : 'false'"
        :aria-label="privacy.isLocal.value
          ? 'Privacidade local ativa: nada sai do dispositivo sem opt-in'
          : 'Privacidade em modo remoto'"
        role="status"
        aria-live="polite"
      />

      <!--
        Trust line: copy literal exigida por HLOS-104.
        CC-08 (motivação cultivada): linguagem informativa, sem pressão.
      -->
      <p class="hlos-privacy-footer__trust" data-testid="privacy-trustline">
        {{ privacy.trustLine.value }}
      </p>

      <!--
        HLOS-127 + HLOS-172: dois links explícitos para Privacy/LGPD:
        1. Política de Privacidade (privacy-policy.html) — full doc
        2. Termos LGPD (lgpd-terms.html) — LGPD compliance (Lei Geral de Proteção de Dados)

        HLOS-172: navegação INTERNA (sem target=_blank). No WKWebView/Capacitor iOS,
        target="_blank" sem InAppBrowser plugin delega para o iOS, que abre Safari
        (ou outro app que registrou scheme https) — o usuário sai do Lume. Os HTMLs
        estão servidos pelo próprio bundle em /privacy-policy.html e /lgpd-terms.html,
        então navegação padrão no WKWebView renderiza DENTRO do app.

        Constitucional:
        - Princípio X (Dignidade): manter usuário no app, não dispersar para Safari.
        - CC-09 (privacy disclosure explícita): link permanece visível e clicável.
      -->
      <a
        href="/privacy-policy.html"
        class="hlos-privacy-footer__link"
        data-testid="privacy-policy-link"
        aria-label="Política de privacidade"
      >
        Política
      </a>
      <a
        href="/lgpd-terms.html"
        class="hlos-privacy-footer__link"
        data-testid="lgpd-terms-link"
        aria-label="Termos LGPD"
      >
        LGPD
      </a>
      <!--
        HLOS-188: build identifier (versão do bundle). Cada deploy gera
        um id único (timestamp). Aparece no footer para o usuário
        verificar qual versão está rodando.
      -->
      <span
        class="hlos-privacy-footer__build-id"
        data-testid="build-id"
        :title="`Build: ${BUILD_ID}`"
      >
        build {{ BUILD_ID }}
      </span>
    </div>
  </footer>
</template>

<style scoped>
.hlos-privacy-footer {
  margin-top: 2rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid color-mix(in srgb, currentColor 20%, transparent);
  opacity: 0.85;
}
.hlos-privacy-footer__row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.hlos-privacy-footer__badge {
  flex-shrink: 0;
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--hlos-color-success);
  /* Pulso suave — informativo, não dark pattern (CC-06).
     2s = lento; opacity 0.6→1 = sutil; sem mudança de cor. */
  animation: hlos-privacy-pulse 2s ease-in-out infinite;
}
@keyframes hlos-privacy-pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
.hlos-privacy-footer__trust {
  margin: 0;
  font-size: 0.85em;
  line-height: 1.4;
  opacity: 0.85;
}

.hlos-privacy-footer__link {
  /* HLOS-59: link explícito para /privacy-policy.html */
  margin-left: auto;
  font-size: 0.85em;
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  opacity: 0.7;
  transition: opacity 0.15s ease;
  white-space: nowrap;
}
.hlos-privacy-footer__link:hover,
.hlos-privacy-footer__link:focus-visible {
  opacity: 1;
}

/* HLOS-188: build identifier (mono-space, dim color) */
.hlos-privacy-footer__build-id {
  margin-left: auto;
  font-size: 0.7em;
  font-family: ui-monospace, "SF Mono", Menlo, Monaco, "Courier New", monospace;
  opacity: 0.5;
  white-space: nowrap;
}

/* Preferência do usuário: desliga a animação se solicitado. */
@media (prefers-reduced-motion: reduce) {
  .hlos-privacy-footer__badge {
    animation: none;
    opacity: 1;
  }
}
</style>
